import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userToken, setUserToken] = useState(null);

    useEffect(() => {
        // Check if user is logged in on mount
        const token = localStorage.getItem('userToken');
        const userData = localStorage.getItem('userData');
        
        if (token && userData) {
            setUserToken(token);
            setUser(JSON.parse(userData));
        }
        setIsLoading(false);
    }, []);

    const signIn = async (userId, token) => {
        try {
            // Store the token and user ID
            localStorage.setItem('userToken', token);
            localStorage.setItem('userId', userId);
            
            const userData = { userId };
            localStorage.setItem('userData', JSON.stringify(userData));
            
            // Flush state synchronously so callers can await this and be sure
            // userToken is set before they call navigate().
            await new Promise((resolve) => {
                setUserToken(token);
                setUser(userData);
                // next microtask — React will have committed the state update
                setTimeout(resolve, 0);
            });
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.message || 'Login failed' 
            };
        }
    };

    const signOut = () => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('userData');
        setUserToken(null);
        setUser(null);
    };

    const signUp = async (userData) => {
        try {
            const response = await api.post('/api/Auth/register', userData);
            return { success: true, data: response.data };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.message || 'Registration failed' 
            };
        }
    };

    return (
        <AuthContext.Provider 
            value={{ 
                user, 
                userToken, 
                isLoading, 
                signIn, 
                signOut, 
                signUp 
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
